// ── Intake Questionnaire ──────────────────────────────────────
// Single-page form rendered between Goal and Plan in the New Workflow
// flow. Modeled on Claude Design's guided intake: all questions at once
// (no rounds), mixed input types, chip-first with free-text escape
// hatches, smart defaults pre-selected, Continue always enabled —
// unanswered questions are simply skipped.
//
// The model generates the questions server-side; this component is a
// dumb renderer of the schema (SPEC-GUIDED-INTAKE.md, single-page rev):
//   { id, question, subtitle?, type: 'text'|'chips', placeholder?,
//     multiSelect?, options: [{label}], defaultSelected?: [label] }
//
// Props:
//   title      — contextual heading ("Quick questions about your pitch deck")
//   questions  — full questionnaire
//   onSubmit(answers) — answers: [{ id, selected: [labels], other: string }]
//   onSkip     — proceed straight to Plan via today's orchestrate path
//   loading    — disables inputs while the brief is generating

import React, { useState, useCallback } from 'react';

export default function IntakeInterview({ title, questions, onSubmit, onSkip, loading }) {
  // { [questionId]: { selected: Set<label>, other: string } } — seeded
  // with the server's smart defaults so confident picks come pre-selected.
  const [answers, setAnswers] = useState(() => {
    const seed = {};
    for (const q of questions) {
      if (q.defaultSelected?.length) {
        seed[q.id] = { selected: new Set(q.defaultSelected), other: '' };
      }
    }
    return seed;
  });

  const toggleOption = useCallback((q, label) => {
    setAnswers(prev => {
      const cur = prev[q.id] || { selected: new Set(), other: '' };
      const selected = new Set(cur.selected);
      if (q.multiSelect) {
        if (selected.has(label)) selected.delete(label);
        else selected.add(label);
      } else {
        if (selected.has(label)) selected.clear();
        else { selected.clear(); selected.add(label); }
      }
      return { ...prev, [q.id]: { ...cur, selected } };
    });
  }, []);

  const setOther = useCallback((qId, text) => {
    setAnswers(prev => ({
      ...prev,
      [qId]: { ...(prev[qId] || { selected: new Set() }), other: text },
    }));
  }, []);

  const answeredCount = questions.filter(q => {
    const a = answers[q.id];
    return !!a && (a.selected?.size > 0 || (a.other || '').trim().length > 0);
  }).length;

  const handleSubmit = () => {
    if (loading) return;
    onSubmit(questions.map(q => {
      const a = answers[q.id] || { selected: new Set(), other: '' };
      return { id: q.id, selected: Array.from(a.selected || []), other: (a.other || '').trim() };
    }));
  };

  return (
    <div className="wfs-iv">
      <div className="wfs-iv-header">
        <h2 className="wfs-iv-title">{title || 'A few quick questions'}</h2>
        <p className="wfs-iv-sub">
          Answer what you can — skip anything, I'll use judgment for the rest.
        </p>
      </div>

      <div className="wfs-iv-questions">
        {questions.map(q => {
          const a = answers[q.id] || { selected: new Set(), other: '' };
          return (
            <div className="wfs-iv-q" key={q.id}>
              <div className="wfs-iv-question">{q.question}</div>
              {q.subtitle && <div className="wfs-iv-subtitle">{q.subtitle}</div>}

              {q.type === 'text' ? (
                <textarea
                  className="wfs-iv-textarea"
                  placeholder={q.placeholder || 'Your answer…'}
                  value={a.other || ''}
                  rows={2}
                  disabled={loading}
                  onChange={e => setOther(q.id, e.target.value)}
                />
              ) : (
                <div className="wfs-iv-chips" role={q.multiSelect ? 'group' : 'radiogroup'}>
                  {q.options.map(opt => {
                    const selected = a.selected?.has(opt.label);
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        role={q.multiSelect ? 'checkbox' : 'radio'}
                        aria-checked={!!selected}
                        disabled={loading}
                        className={`wfs-iv-pill ${selected ? 'wfs-iv-pill--selected' : ''}`}
                        onClick={() => toggleOption(q, opt.label)}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                  {/* Inline "Other…" — lives IN the chip row, Claude Design style */}
                  <input
                    type="text"
                    className="wfs-iv-pill-other"
                    placeholder="Other…"
                    value={a.other || ''}
                    disabled={loading}
                    onChange={e => setOther(q.id, e.target.value)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="wfs-iv-actions">
        <button type="button" className="wfs-btn wfs-btn--ghost" onClick={onSkip} disabled={loading}>
          Skip questions →
        </button>
        <div className="wfs-iv-actions-right">
          <span className="wfs-iv-answered">{answeredCount}/{questions.length} answered</span>
          <button
            type="button"
            className="wfs-btn wfs-btn--primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Thinking…' : 'Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}
